import {
  type Coordinate,
  type FileInputMetadata,
  type RawTelemetryData,
  type SessionFeatures,
  processRawTelemetryWithTrackDto,
  isEmptyGuid,
} from '@pitbrain/shared'
import { type ApiFunctions } from '../../api/useApi'
import { useTrackSessionContext } from '../trackSession/TrackSessionContext'

export const GPS_ONLY_FEATURES: SessionFeatures = {
  hasLeanAngle: false,
  hasVideo: false,
}

export const ON_ANOTHER_TRACK =
  'The uploaded file is from a different track than the session that is already loaded.'

type NewSession = FileInputMetadata & { sessionFeatures: SessionFeatures }

const validateTrackCompatibility = (
  existingTrackId: string | undefined,
  newTrackId: string | undefined
): void => {
  const existingIsUnknown = isEmptyGuid(existingTrackId)
  const newIsUnknown = !newTrackId

  const tracksDiffer =
    existingIsUnknown !== newIsUnknown ||
    (!existingIsUnknown && !newIsUnknown && existingTrackId !== newTrackId)

  if (tracksDiffer) {
    throw Error(ON_ANOTHER_TRACK)
  }
}

const firstCoordinateOf = (
  rawTelemetry: RawTelemetryData
): Coordinate | null => {
  const firstPoint = rawTelemetry.gps?.[0]
  if (firstPoint == null) return null
  return { latitude: firstPoint.latitude, longitude: firstPoint.longitude }
}

export const useCreateSession = ({
  trackApi,
}: Pick<ApiFunctions, 'trackApi'>) => {
  const { setTrackSessions, trackSessions } = useTrackSessionContext()

  return async (
    rawTelemetry: RawTelemetryData,
    fileMetadata: NewSession
  ): Promise<boolean> => {
    const firstCoordinate = firstCoordinateOf(rawTelemetry)
    if (firstCoordinate == null) return false

    const track = await trackApi.findTrack(firstCoordinate)
    if (trackSessions?.length) {
      validateTrackCompatibility(trackSessions[0].track?.id, track?.id)
    }

    const result = await processRawTelemetryWithTrackDto(
      rawTelemetry,
      track,
      fileMetadata
    )

    setTrackSessions((prevState) => [...prevState, result.trackSession])
    return true
  }
}
